/**
 * Ready-made HMRC claim letter. Filled from the donor's account so they can
 * download PDF or Word instead of drafting it themselves. National Insurance
 * is an optional input from the browser session only, never stored. The
 * letter leaves a signature line for the donor to sign after printing.
 *
 * Written claims always enclose the same schedule of Gift Aided donations
 * when we have rows. The letter mentions that enclosure in the same words
 * either side of £10,000. PDF and Word share this content so the two files
 * cannot drift.
 */

import { DateTime } from 'luxon'
import { formatMoneyFromPence } from '@/helpers/moneyDisplay'
import type { DonorDonation } from '@/types/donorFinancialYear'
import {
  formatNationalInsuranceNumber,
  parseNationalInsuranceNumber,
} from '@/helpers/claimTaxBack/nationalInsurance'

export type ClaimLetterDonationRow = {
  date: string
  charity: string
  amount: string
}

export type ClaimLetterParticular = {
  label: string
  value: string
}

export type ClaimLetterContent = {
  senderName: string
  senderAddressLines: string[]
  letterDate: string
  yearLabel: string
  amountGiven: string
  greeting: string
  subject: string
  opening: string
  totalGiven: string
  rateClaim: string
  particularsHeading: string
  particulars: ClaimLetterParticular[]
  enclosureNote: string
  giftAidCover: string
  request: string
  closing: string
  preparedNote: string
  includeSchedule: boolean
  scheduleRequired: boolean
  scheduleEyebrow: string
  scheduleHeading: string
  scheduleSubheading: string
  scheduleIntro: string
  scheduleCount: string
  scheduleFooter: string
  tableDate: string
  tableCharity: string
  tableAmount: string
  tableTotal: string
  donations: ClaimLetterDonationRow[]
}

export type ClaimLetterInput = {
  senderName: string
  senderAddressLines: string[]
  letterDate: string
  yearLabel: string
  givenPence: number
  ratePhrase: string
  /** True when HMRC requires every donation listed (£10,000 or more given). */
  includeItemisedList: boolean
  donations: DonorDonation[]
  /** Formatted or compact National Insurance number. Omitted so the letter keeps a blank. */
  nationalInsuranceNumber?: string | null
}

const NI_BLANK = '____________________'
const NAME_BLANK = '____________________'

/** Suggested download name, e.g. gift-aid-tax-relief-letter-2025-26.pdf */
export function claimLetterFilename(
  yearLabel: string,
  extension: 'pdf' | 'docx'
): string {
  const safeYear = yearLabel.replace(/\//g, '-')
  return `gift-aid-tax-relief-letter-${safeYear}.${extension}`
}

export function formatClaimLetterDate(
  iso: string = DateTime.now().toISO()!
): string {
  const dt = DateTime.fromISO(iso, { zone: 'Europe/London' })
  return (dt.isValid ? dt : DateTime.now().setZone('Europe/London')).toFormat(
    'd MMMM yyyy'
  )
}

function formatDonationDate(iso: string): string {
  const dt = DateTime.fromISO(iso, { zone: 'Europe/London' })
  return dt.isValid ? dt.toFormat('d MMM yyyy') : iso
}

function usableAddressLines(lines: string[]): string[] {
  return lines
    .map((line) => line.trim())
    .filter((line) => line.length > 0 && line !== ',')
}

function donationRows(donations: DonorDonation[]): ClaimLetterDonationRow[] {
  return [...donations]
    .sort((a, b) => a.time.localeCompare(b.time))
    .map((donation) => ({
      date: formatDonationDate(donation.time),
      charity: donation.charity.trim() || 'Charity',
      amount: formatMoneyFromPence(donation.amount),
    }))
}

function donationCountLabel(count: number): string {
  if (count === 1) return '1 Gift Aided donation'
  return `${count} Gift Aided donations`
}

/**
 * Builds the letter a PAYE donor posts to HMRC. Same content is used for PDF
 * and Word so the two files cannot drift.
 */
export function buildClaimLetterContent(
  input: ClaimLetterInput
): ClaimLetterContent {
  const amountGiven = formatMoneyFromPence(input.givenPence)
  const senderName = input.senderName.trim()
  const parsedNi = parseNationalInsuranceNumber(
    input.nationalInsuranceNumber ?? ''
  )
  const niDisplay = parsedNi
    ? formatNationalInsuranceNumber(parsedNi)
    : NI_BLANK
  const rows = donationRows(input.donations)
  const includeSchedule = rows.length > 0
  const enclosureNote = includeSchedule
    ? 'I have enclosed a schedule of the Gift Aided donations that make up this total.'
    : ''

  return {
    senderName,
    senderAddressLines: usableAddressLines(input.senderAddressLines),
    letterDate: input.letterDate,
    yearLabel: input.yearLabel,
    amountGiven,
    greeting: 'Dear Sir or Madam',
    subject: `Re: Claim for Gift Aid tax relief, tax year ${input.yearLabel}`,
    opening: `I am writing to claim Income Tax relief at the ${input.ratePhrase} on charitable donations I made under Gift Aid during the ${input.yearLabel} tax year.`,
    totalGiven: `During this tax year, I made qualifying Gift Aid donations totalling ${amountGiven}.`,
    rateClaim: `I paid Income Tax at the ${input.ratePhrase} during this tax year and would therefore like to claim the additional tax relief due on these Gift Aid donations.`,
    particularsHeading: 'Particulars of the claim',
    particulars: [
      { label: 'Name', value: senderName || NAME_BLANK },
      { label: 'National Insurance number', value: niDisplay },
      { label: 'Tax year', value: input.yearLabel },
      { label: 'Income tax paid at', value: `the ${input.ratePhrase}` },
      { label: 'Total Gift Aided donations', value: amountGiven },
    ],
    enclosureNote,
    giftAidCover:
      'All of the donations in this claim were made under Gift Aid declarations, and I paid sufficient UK Income Tax and/or Capital Gains Tax to cover the Gift Aid claimed by the charities.',
    request:
      'Please calculate any additional Income Tax relief due on these Gift Aid donations and apply it to my tax record. Please let me know if you require any further information or evidence relating to these donations.',
    closing: 'Yours faithfully',
    preparedNote: parsedNi
      ? ''
      : 'Add your National Insurance number and sign this letter before you post it.',
    includeSchedule,
    scheduleRequired: input.includeItemisedList,
    scheduleEyebrow: 'Enclosure',
    scheduleHeading: 'Schedule of Gift Aided donations',
    scheduleSubheading: `Tax year ${input.yearLabel}`,
    scheduleIntro:
      'These donations make up the total Gift Aided donations in the accompanying letter.',
    scheduleCount: donationCountLabel(rows.length),
    scheduleFooter: `Enclosure to the letter dated ${input.letterDate}`,
    tableDate: 'Date',
    tableCharity: 'Charity',
    tableAmount: 'Amount',
    tableTotal: 'Total',
    donations: rows,
  }
}
